import {
  createContext,
  useCallback,
  useState,
  useMemo,
  useContext,
} from "react";

// Asset
import blackout from "../assets/images/grayscale/blackLive.jpeg";

import { useExamStatus } from "../hooks/useExamStatus";
import { DeviceStagesTimeline, getActiveStep } from "../utils/signalRHelper";
import { examCardEnums, algorithmTypes } from "../enums";

const LiveUpdateStatusContext = createContext();

export const useLiveUpdateStatusContext = () => {
  const context = useContext(LiveUpdateStatusContext);
  if (!context) {
    throw new Error(
      "useLiveUpdateStatusContext must be used within LiveUpdateStatusContextProvider"
    );
  }

  return context;
};

export const LiveUpdateStatusProvider = ({
  devicesWithExams = [],
  defaultProps, // used mainly for testing
  children,
}) => {
  // values that persist after the exam is complete
  const [deviceId, setDeviceId] = useState(null);
  const [reportUrl, setReportUrl] = useState(null);

  // current device data
  const device = useMemo(() => {
    const deviceWithExam = devicesWithExams?.find(
      (examDevice) => examDevice?.id === deviceId
    );
    return defaultProps?.device ?? deviceWithExam ?? null;
  }, [defaultProps, devicesWithExams, deviceId]);

  // current exam data
  const exam = useMemo(() => {
    const deviceWithExam = devicesWithExams?.find(
      (examDevice) => examDevice?.id === deviceId
    );
    const examToUse =
      defaultProps?.exam ?? deviceWithExam?.associatedExam?.exam ?? null;

    // update states that will persist after the exam is complete
    if (examToUse && !reportUrl) {
      setReportUrl(`/patients/${examToUse?.patient?.id}/exam/${examToUse?.id}`);
    }

    return examToUse;
  }, [defaultProps, devicesWithExams, deviceId, reportUrl]);

  const currentExamSection = useMemo(() => exam?.nextSection, [exam]);

  const examType = useMemo(
    () => currentExamSection?.type,
    [currentExamSection]
  );
  const examAlgorithm = useMemo(
    () => currentExamSection?.algorithm,
    [currentExamSection]
  );

  const [open, setOpen] = useState(defaultProps?.open ?? false);
  const [fullScreenModal, setFullScreenModal] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const [shouldDisplayNavigation, setShouldDisplayNavigation] = useState(false);
  const [examReportLive, setExamReportLive] = useState(undefined);
  const [isVrMode, setIsVrMode] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [isNotStarted, setIsNotStarted] = useState(false);
  const [isRightCompleted, setIsRightCompleted] = useState(false);
  const [isTerminated, setIsTerminated] = useState(false);

  // WebRtc
  const [peerConnection, setPeerConnection] = useState(null);
  const [dataChannel, setDataChannel] = useState(null);
  const [imageSrc, setImageSrc] = useState(blackout);

  // the possible steps
  // remove the calibration stage option, when the exam has "calibration" as false
  // remove the tutorial stage option, when the exam has "requiresTutorial" as false
  const possibleSteps = useMemo(
    () =>
      Object.values(DeviceStagesTimeline)
        .filter(
          (stage) =>
            stage !== DeviceStagesTimeline.Calibration ||
            (stage === DeviceStagesTimeline.Calibration &&
              currentExamSection?.calibration)
        )
        .filter(
          (stage) =>
            stage !== DeviceStagesTimeline.Tutorial ||
            (stage === DeviceStagesTimeline.Tutorial &&
              currentExamSection?.requiresTutorial)
        )
        // Todo - Completed -> Terminated
        .filter((stage) => stage !== DeviceStagesTimeline.Terminated),
    [currentExamSection]
  );

  const { getExamStatus } = useExamStatus();
  const examStatus = useMemo(
    () => getExamStatus(exam, devicesWithExams),
    [devicesWithExams, exam, getExamStatus]
  );

  // the active step
  const activeStep = useMemo(
    () =>
      defaultProps?.activeStep ??
      getActiveStep(exam, possibleSteps, examStatus),
    [exam, possibleSteps, examStatus, defaultProps?.activeStep]
  );

  const isOnline = useMemo(
    () => device?.deviceStatus?.stage?.stageType,
    [device?.deviceStatus?.stage?.stageType]
  );

  const inTutorial = activeStep.value === DeviceStagesTimeline.Tutorial;
  const inCalibration = activeStep.value === DeviceStagesTimeline.Calibration;
  const bothControllersDisconnected =
    device?.deviceStatus?.controllerBatteryL === -1 &&
    device?.deviceStatus?.controllerBatteryR === -1;
  const hmdInProximity = device?.deviceStatus?.hmdInProximity;

  const isVisualField = useMemo(() => {
    return (
      examType?.startsWith("VisualField") &&
      examType !== examCardEnums.VISUAL_FIELD_ESTERMAN.type
    );
  }, [examType]);

  const isEsterman = useMemo(() => {
    return examType === examCardEnums.VISUAL_FIELD_ESTERMAN.type;
  }, [examType]);

  const isVisualAcuity = useMemo(() => {
    return examType === examCardEnums.VISUAL_ACUITY.type;
  }, [examType]);

  const isColorVision = useMemo(() => {
    return examType === examCardEnums.COLOR_VISION.type;
  }, [examType]);

  const isPelirobson = useMemo(() => {
    return examAlgorithm === algorithmTypes.PELLI_ROBSON;
  }, [examAlgorithm]);

  const isTumbling = useMemo(() => {
    return examAlgorithm === algorithmTypes.TUMBLING_E;
  }, [examAlgorithm]);

  const isLandoltC = useMemo(() => {
    return examType === algorithmTypes.LANDOLT_C;
  }, [examType]);

  const isBaileyLovie = useMemo(() => {
    return examAlgorithm === algorithmTypes.BAILEY_LOVIE;
  }, [examAlgorithm]);

  const isSnellen = useMemo(() => {
    return examAlgorithm === algorithmTypes.SNELLEN;
  }, [examAlgorithm]);

  const isSingleThreshold = useMemo(() => {
    return examAlgorithm === algorithmTypes.FULL_120;
  }, [examAlgorithm]);

  const isSynoptophore = useMemo(() => {
    return examAlgorithm === algorithmTypes.BINOCULAR_VISION;
  }, [examAlgorithm]);

  const isRAPD = useMemo(() => {
    return examType === examCardEnums.RAPD.type;
  }, [examType]);

  // tasks to execute when closing the modal
  const resetTasks = useCallback(() => {
    // resetting some states
    setIsVrMode(false);
    setExamReportLive(undefined);
    setPercentage(0);
    setIsPaused(false);
    setIsNotStarted(false);
    setIsConfirmation(false);
    setIsRightCompleted(false);
    setIsTerminated(false);
    setShouldDisplayNavigation(false);
  }, []);

  const handleViewLiveUpdateClick = useCallback(
    (deviceId) => {
      setDeviceId(deviceId);
      setOpen(true);
    },
    [setOpen, setDeviceId]
  );

  // Stop WebRTC connection
  const stopWebRTC = useCallback(() => {
    // Close the data channel
    if (dataChannel) {
      dataChannel.close();
      setDataChannel(null);
    }

    // Close the peer connection
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }

    // Clear display
    setImageSrc(blackout);
  }, [dataChannel, peerConnection, setDataChannel, setPeerConnection]);

  return (
    <LiveUpdateStatusContext.Provider
      value={{
        exam,
        currentExamSection,
        reportUrl,
        open,
        device,
        examStatus,
        isVrMode,
        isPaused,
        isConfirmation,
        isNotStarted,
        isTerminated,
        isRightCompleted,
        inTutorial,
        inCalibration,
        isOnline,
        bothControllersDisconnected,
        hmdInProximity,
        activeStep,
        possibleSteps,
        examReportLive,
        percentage,
        shouldDisplayNavigation,
        fullScreenModal,
        peerConnection,
        dataChannel,
        imageSrc,
        isVisualField,
        isEsterman,
        isVisualAcuity,
        isColorVision,
        isPelirobson,
        isTumbling,
        isLandoltC,
        isBaileyLovie,
        isSynoptophore,
        isRAPD,
        isSnellen,
        isSingleThreshold,
        actions: {
          stopWebRTC,
          setImageSrc,
          setDataChannel,
          setPeerConnection,
          setFullScreenModal,
          setExamReportLive,
          setIsVrMode,
          setIsPaused,
          setPercentage,
          setIsConfirmation,
          setIsNotStarted,
          setIsTerminated,
          setIsRightCompleted,
          setShouldDisplayNavigation,
          setOpen,
          handleViewLiveUpdateClick,
          resetTasks,
        },
      }}
    >
      {children}
    </LiveUpdateStatusContext.Provider>
  );
};
